import { Button, Badge, Tooltip, ColorPicker } from "antd";
import {
  ClearOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  AimOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useHighlight } from "../../../hooks/useHighlight";
import { useConfirmModal } from "../../../hooks/useConfirmModal";

const IconButton = styled(Button)<{ $active?: boolean }>`
  height: 34px;
  min-width: 34px;
  padding: 0;
  border-radius: 8px;
  font-size: 17px;

  background: ${({ $active }) => ($active ? "#8e1616" : "#393e46")} !important;
  color: #eeeeee !important;
  border: 1px solid #8e1616 !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  &:hover:not(:disabled) {
    background: #222831 !important;
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
`;

const ColorPickerTrigger = styled(Button)<{ $color: string }>`
  height: 34px;
  min-width: 34px;
  padding: 0;
  border-radius: 8px;
  font-size: 17px;
  background: #393e46 !important;
  color: #eeeeee !important;
  border: 2px solid ${({ $color }) => $color} !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  &:hover:not(:disabled) {
    background: #222831 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
`;

const ColorDot = styled.span<{ $color: string }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid rgba(255, 255, 255, 0.5);
  display: inline-block;
  flex-shrink: 0;
`;

export default function HighlightPanel() {
  const {
    isHighlightMode,
    toggleHighlightMode,
    highlightedIds,
    highlightColor,
    setHighlightColor,
    clearHighlights,
    showOnlyHighlighted,
    toggleShowOnlyHighlighted,
  } = useHighlight();

  const count = highlightedIds.size;
  const { confirm } = useConfirmModal();

  return (
    <>
      <Tooltip
        title={isHighlightMode ? "Exit highlight mode" : "Highlight markers"}
      >
        <IconButton
          $active={isHighlightMode}
          onClick={toggleHighlightMode}
          icon={<AimOutlined />}
          aria-label="Toggle highlight mode"
        />
      </Tooltip>

      <Tooltip title="Pick highlight color">
        <ColorPicker
          value={highlightColor}
          onChange={(color) => setHighlightColor(color.toHexString())}
          disabledAlpha
        >
          <ColorPickerTrigger
            $color={highlightColor}
            aria-label="Pick highlight color"
          >
            <ColorDot $color={highlightColor} />
          </ColorPickerTrigger>
        </ColorPicker>
      </Tooltip>

      <Tooltip
        title={
          count > 0 ? "Jump to highlighted markers" : "No highlighted markers"
        }
      >
        <IconButton
          $active={false}
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent("fit-highlighted-markers", {
                detail: { ids: Array.from(highlightedIds) },
              }),
            )
          }
          disabled={count === 0}
          icon={<GlobalOutlined />}
          aria-label="Jump to highlighted markers"
        />
      </Tooltip>

      <Tooltip
        title={
          showOnlyHighlighted
            ? "Show all markers"
            : "Show only highlighted markers"
        }
      >
        <IconButton
          $active={showOnlyHighlighted}
          onClick={toggleShowOnlyHighlighted}
          disabled={count === 0}
          icon={
            showOnlyHighlighted ? <EyeInvisibleOutlined /> : <EyeOutlined />
          }
          aria-label="Toggle show only highlighted"
        />
      </Tooltip>

      <Tooltip
        title={
          count > 0 ? `Clear all highlights (${count})` : "Clear all highlights"
        }
      >
        <Badge count={count} color="#8e1616" size="small">
          <IconButton
            $active={false}
            onClick={() =>
              confirm({
                question: `Are you sure you want to clear all highlights (${count})?`,
                onOk: clearHighlights,
              })
            }
            disabled={count === 0}
            icon={<ClearOutlined />}
            aria-label="Clear highlights"
          />
        </Badge>
      </Tooltip>
    </>
  );
}
